import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { topicsAPI, questionsAPI } from "../api/index.js";
import { useAuth } from "../hooks/useAuth.jsx";
import SEO from "../components/SEO.jsx";
import { TOPIC_DESCRIPTIONS, TOPIC_NAMES, TOPIC_SLUGS } from "../constants/topics.js";

const G = {
  bg: "#0A0A0A", surface: "#111111", border: "#1E1E1E",
  text: "#E4E4DC", muted: "#666", accent: "#00C896",
  purple: "#7C6FFF", danger: "#FF6B6B",
  font: "'Sora', sans-serif", mono: "'DM Mono', monospace",
};

const SITE_URL = "https://askfogra.com";
const PAGE_SIZE = 20;

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "most_viewed", label: "Most Viewed" },
  { value: "most_answered", label: "Most Answered" },
  { value: "top_voted", label: "Top Voted" },
];

function timeAgo(dateStr) {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

const Avatar = ({ initials, size = 20 }) => (
  <div style={{ width: size, height: size, borderRadius: "50%", background: "#1A1A1A", border: `1.5px solid ${G.accent}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.4, fontWeight: 700, color: G.accent, fontFamily: G.mono, flexShrink: 0 }}>
    {initials?.slice(0, 2).toUpperCase()}
  </div>
);

const QuestionRow = ({ q }) => (
  <Link to={`/q/${q.slug}`} style={{ textDecoration: "none" }}>
    <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 10, padding: "16px 18px", marginBottom: 10, transition: "border-color 0.15s" }}
      onMouseOver={e => e.currentTarget.style.borderColor = "#2A2A2A"}
      onMouseOut={e => e.currentTarget.style.borderColor = G.border}>
      <div style={{ fontSize: 15, fontWeight: 600, color: G.text, marginBottom: 10, lineHeight: 1.4 }}>{q.title}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Avatar initials={q.author_username} />
          <span style={{ fontSize: 12, color: G.muted }}>{q.author_username}</span>
        </div>
        <span style={{ fontSize: 12, color: "#3A3A3A", fontFamily: G.mono }}>💬 {q.answer_count ?? 0} answer{q.answer_count === 1 ? "" : "s"}</span>
        <span style={{ fontSize: 12, color: "#3A3A3A", fontFamily: G.mono }}>👁 {q.views ?? 0} view{q.views === 1 ? "" : "s"}</span>
        <span style={{ fontSize: 12, color: "#3A3A3A", fontFamily: G.mono }}>{timeAgo(q.created_at)}</span>
      </div>
    </div>
  </Link>
);

export default function TopicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const sort = searchParams.get("sort") || "newest";

  // The 10 topic slugs are fixed and known at build time. A slug in this
  // list is ALWAYS a real, indexable page — full stop — regardless of what
  // happens to the API call. This is a synchronous, network-independent
  // guarantee that a valid topic can never fall into the noindex/not-found
  // branch just because a request timed out or the backend cold-started.
  const isKnownTopic = TOPIC_SLUGS.includes(slug);

  const [topic, setTopic] = useState(null);
  const [allTopics, setAllTopics] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!searchParams.get("sort")) {
      const next = new URLSearchParams(searchParams);
      next.set("sort", "newest");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { topicsAPI.list().then(r => setAllTopics(r.data.topics || [])).catch(() => {}); }, []);

  useEffect(() => {
    // Fetch the topic and its questions independently: the topic call is
    // the only thing that determines notFound/noindex. If the questions
    // call fails on its own (e.g. a transient API timeout), a perfectly
    // valid topic must not get marked not-found and lose its indexability.
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setNotFound(false);
    topicsAPI.get(slug)
      .then(tRes => {
        if (requestId !== requestIdRef.current) return;
        setTopic(tRes.data.topic);
        return questionsAPI.list({ topic: slug, page: 1, limit: PAGE_SIZE, sort })
          .then(qRes => {
            if (requestId !== requestIdRef.current) return;
            setQuestions(qRes.data.questions || []);
            setPage(1);
            setPages(qRes.data.pages || 1);
          })
          .catch(() => {
            if (requestId !== requestIdRef.current) return;
            setQuestions([]);
            setPage(1);
            setPages(1);
          });
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        if (isKnownTopic) {
          // The topic API call itself failed (timeout, cold start, 5xx —
          // whatever), but this slug is one of the 10 real topics. Render a
          // real, indexable page from static fallback data instead of a
          // false 404/noindex. The question list just stays empty.
          setTopic({ name: TOPIC_NAMES[slug], slug, question_count: 0, color: null });
          setQuestions([]);
          setPage(1);
          setPages(1);
        } else {
          setNotFound(true);
        }
      })
      .finally(() => { if (requestId === requestIdRef.current) setLoading(false); });
  }, [slug, sort, isKnownTopic]);

  const loadMore = useCallback(async () => {
    if (loadingMore || page >= pages) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const res = await questionsAPI.list({ topic: slug, page: nextPage, limit: PAGE_SIZE, sort });
      setQuestions(prev => [...prev, ...(res.data.questions || [])]);
      setPage(nextPage);
      setPages(res.data.pages || pages);
    } catch { /* keep current list on failure */ }
    finally { setLoadingMore(false); }
  }, [loadingMore, page, pages, slug, sort]);

  const handleSortChange = (value) => {
    const next = new URLSearchParams(searchParams);
    next.set("sort", value);
    setSearchParams(next);
  };

  const handleAsk = () => navigate(user ? "/ask" : "/login");

  // No <SEO>/<Helmet> render here on purpose: react-helmet-async doesn't
  // recognize the prerendered static meta tags as its own (they lack its
  // `data-rh` marker) and will ADD a second, separate one rather than
  // replace them. That's harmless when both agree, but mounting an SEO
  // instance here just to reassert index,follow is unnecessary churn — the
  // static prerendered tags already say index,follow and nothing touches
  // them while no <Helmet> is mounted.
  if (loading) return <div style={{ textAlign: "center", padding: "80px 0", color: G.muted, fontFamily: G.font, background: G.bg, minHeight: "60vh" }}>Loading...</div>;
  if (notFound || !topic) return (
    <div style={{ textAlign: "center", padding: "80px 16px", background: G.bg, minHeight: "60vh" }}>
      <SEO title="Topic Not Found" noindex />
      <div style={{ color: G.danger, fontFamily: G.font, marginBottom: 16 }}>Topic not found.</div>
      <Link to="/feed" style={{ color: G.accent, textDecoration: "none", fontFamily: G.mono }}>← Back to Feed</Link>
    </div>
  );

  const description = TOPIC_DESCRIPTIONS[topic.slug] || topic.description || `Questions about ${topic.name}.`;
  const canonical = `/topics/${topic.slug}`;
  const seoTitle = `${topic.name} Questions & Answers`;
  const seoDescription = `Browse ${topic.question_count} ${topic.name.toLowerCase()} questions on askFOGRA. ${description}`;
  const relatedTopics = allTopics.filter(t => t.slug !== topic.slug);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": seoTitle,
    "description": seoDescription,
    "url": `${SITE_URL}${canonical}`,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": topic.question_count,
      "itemListElement": questions.map((q, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "url": `${SITE_URL}/q/${q.slug}`,
        "name": q.title,
      })),
    },
  };

  return (
    <div style={{ background: G.bg, minHeight: "100vh" }}>
      <SEO title={seoTitle} description={seoDescription} url={canonical} jsonLd={jsonLd} />
      <div style={{ maxWidth: 1040, margin: "0 auto", padding: "28px 16px", fontFamily: G.font, display: "flex", gap: 28, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 640px", minWidth: 0 }}>
          <Link to="/feed" style={{ textDecoration: "none", color: G.muted, fontSize: 13, fontFamily: G.mono }}>← Back to Feed</Link>

          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "18px 0 6px" }}>
            {topic.color && <span style={{ width: 12, height: 12, borderRadius: "50%", background: topic.color, display: "inline-block" }} />}
            <h1 style={{ fontSize: 26, fontWeight: 800, color: G.text, margin: 0 }}>{topic.name}</h1>
          </div>
          <p style={{ color: G.muted, fontSize: 14, lineHeight: 1.6, marginBottom: 4, maxWidth: 620 }}>{description}</p>
          <p style={{ color: "#3A3A3A", fontSize: 12, fontFamily: G.mono, marginBottom: 20 }}>{topic.question_count} question{topic.question_count !== 1 ? "s" : ""}</p>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {SORT_OPTIONS.map(opt => (
                <button key={opt.value} onClick={() => handleSortChange(opt.value)}
                  style={{
                    background: sort === opt.value ? G.accent : "#1A1A1A",
                    color: sort === opt.value ? "#000" : G.muted,
                    border: `1px solid ${sort === opt.value ? G.accent : "#2A2A2A"}`,
                    borderRadius: 20, padding: "6px 14px", fontSize: 12, fontFamily: G.mono,
                    cursor: "pointer", fontWeight: sort === opt.value ? 700 : 400, transition: "all 0.15s",
                  }}>{opt.label}</button>
              ))}
            </div>
            <button onClick={handleAsk} style={{ background: G.accent, color: "#000", border: "none", borderRadius: 24, padding: "9px 20px", fontSize: 13, fontFamily: G.mono, fontWeight: 700, cursor: "pointer" }}>
              + Ask a Question
            </button>
          </div>

          {questions.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#333" }}>No questions in this topic yet.</div>
          ) : (
            <>
              {questions.map(q => <QuestionRow key={q.id} q={q} />)}
              {page < pages && (
                <div style={{ textAlign: "center", marginTop: 16 }}>
                  <button onClick={loadMore} disabled={loadingMore}
                    style={{ background: "#1A1A1A", border: "1px solid #2A2A2A", color: G.muted, borderRadius: 24, padding: "10px 28px", fontSize: 13, fontFamily: G.mono, cursor: loadingMore ? "default" : "pointer", opacity: loadingMore ? 0.6 : 1 }}>
                    {loadingMore ? "Loading..." : "Load More"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ flex: "0 0 260px", minWidth: 220 }}>
          <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: "16px 18px" }}>
            <div style={{ fontSize: 11, color: G.muted, fontFamily: G.mono, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>Related Topics</div>
            {relatedTopics.length === 0 ? (
              <div style={{ color: "#333", fontSize: 13 }}>—</div>
            ) : relatedTopics.map(t => (
              <Link key={t.id} to={`/topics/${t.slug}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, textDecoration: "none", padding: "8px 0", borderBottom: `1px solid ${G.border}` }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  {t.color && <span style={{ width: 8, height: 8, borderRadius: "50%", background: t.color, flexShrink: 0 }} />}
                  <span style={{ fontSize: 13, color: G.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</span>
                </span>
                <span style={{ fontSize: 11, color: "#3A3A3A", fontFamily: G.mono, flexShrink: 0 }}>{t.question_count}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
